import { useState, useEffect, useRef, useCallback } from 'react';
import AuthService from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import {
  Search,
  Play,
  Pause,
  Download,
  Clock,
  Trash2,
  ChevronDown,
  ChevronRight,
  User,
  Folder,
  Mail,
  Phone,
  Loader2,
  Volume2,
  X,
  RotateCcw,
} from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import biometricAuthService from '@/services/biometricAuthService';
import ENV from '@/lib/env';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AudioFile {
  name: string;
  id: string;           // S3 key — used as unique ID
  created_at: string;
  size: number;
  publicUrl?: string;   // presigned URL fetched on-demand and cached
  survey_reference: string;
  uuid: string;         // patient UUID from API
}

interface PatientInfo {
  id: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  email: string | null;
  mobile_number: string | null;
  date_of_birth: string | null;
  gender: string | null;
}

interface PatientAudio {
  patientId: string;
  patient: PatientInfo | null;   // null while loading / not found
  audioFiles: AudioFile[];
  isExpanded: boolean;
}

const ALLOWED_ROLES = [
  'admin', 'administrator', 'superadmin', 'super admin', 'doctor', 'nurse',
  'technician', 'clinical_reviewer', 'clinical reviewer', 'branchadmin', 'branch admin',
  'hospitaladmin', 'hospital admin', 'staff', 'coordinator', 'receptionist',
  'nurse practitioner', 'physician assistant', 'clinical technician',
  'carecoordinator', 'care coordinator'
];

// Base URL and legacy helpers removed in favor of AuthService

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getPatientDisplayName = (patient: PatientInfo | null, fallbackId: string): string => {
  if (!patient) return fallbackId;
  const name = [patient.first_name, patient.middle_name, patient.last_name].filter(Boolean).join(' ');
  return name || patient.email || fallbackId;
};

const formatAudioTime = (seconds: number): string => {
  if (isNaN(seconds) || !isFinite(seconds) || seconds <= 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

// Legacy fetch helpers replaced by AuthService methods

import MainLayout from '@/layouts/MainLayout';
import { formatPhoneDisplay } from '@/lib/utils';

// ─── Component ────────────────────────────────────────────────────────────────

function PatientAudioManager() {
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('');
  const [patientAudios, setPatientAudios] = useState<PatientAudio[]>([]);
  const [filteredPatientAudios, setFilteredPatientAudios] = useState<PatientAudio[]>([]);
  const [rolesMetadata, setRolesMetadata] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [surveyFilter, setSurveyFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'files'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPlayingId, setCurrentPlayingId] = useState<string | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loadingUrl, setLoadingUrl] = useState<string | null>(null);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const navigate = useNavigate();
  const { selectedLocationId, locations, loading: branchLoading } = useLocation();

  // Derived permission — clinical roles can listen & download; default to true for authorized roles
  const canListen = rolesMetadata.length > 0 
    ? rolesMetadata.find(r => (r.role || r.name || '').toLowerCase() === userRole.toLowerCase())?.is_clinical !== false
    : true;

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchPatientAudios = useCallback(async () => {
    try {
      setLoading(true);

      const user = await AuthService.getCurrentUser();
      if (!user) { navigate('/login'); return; }

      const userProfile = await AuthService.getProfile(user.username).catch(() => null);
      const rawRole = userProfile?.role || (user as any)?.role || 'admin';
      const role = rawRole.toLowerCase().trim();
      const normalizedRole = role.replace(/\s/g, "");

      console.log("🔍 AudioArchives: rawRole:", rawRole, "role:", role, "normalizedRole:", normalizedRole);

      const isAllowed = ALLOWED_ROLES.some(r => {
        const normalizedAllowed = r.toLowerCase().replace(/\s/g, "");
        return normalizedAllowed === normalizedRole;
      }) || role.includes('admin');

      if (!isAllowed) {
        console.warn(`🚫 AudioArchives: Role "${rawRole}" not in ALLOWED_ROLES. Redirecting to dashboard.`);
        navigate('/dashboard');
        return;
      }

      setUserRole(role);

      // Fetch dynamic roles metadata for is_clinical check
      const metadata = await AuthService.getRolesMetadata().catch(() => []);
      setRolesMetadata(metadata || []);

      const queryParams: Record<string, any> = {};
      if (selectedLocationId) {
        const selectedLocationObj = locations.find((l) => Number(l.value) === Number(selectedLocationId));
        const branchId = selectedLocationObj?.branchId;
        const branchName = selectedLocationObj?.branchName || selectedLocationObj?.label?.split(" — ")[1] || "";
        const hospitalName = selectedLocationObj?.hospitalName || selectedLocationObj?.label?.split(" — ")[0] || "";

        if (branchId) {
          queryParams.branch_id = branchId;
          queryParams.branch = branchName || branchId;
        }
        if (hospitalName) {
          queryParams.hospital_name = hospitalName;
          queryParams.hospital = hospitalName;
        }
      }

      console.log("🔍 AudioArchives: Fetching with params:", queryParams);
      let response = await AuthService.listAudioFiles(queryParams);
      console.log("🔍 AudioArchives: API response:", response);

      let rawFiles: any[] =
        response?.data?.files ||
        response?.files ||
        (Array.isArray(response?.data) ? response.data : []) ||
        (Array.isArray(response) ? response : []);

      // If location-filtered query returned 0 files, fallback to fetching all audio files
      if (rawFiles.length === 0 && Object.keys(queryParams).length > 0) {
        console.log("🔍 AudioArchives: Location query returned 0 files, falling back to all audio files...");
        const fallbackResponse = await AuthService.listAudioFiles();
        rawFiles =
          fallbackResponse?.data?.files ||
          fallbackResponse?.files ||
          (Array.isArray(fallbackResponse?.data) ? fallbackResponse.data : []) ||
          (Array.isArray(fallbackResponse) ? fallbackResponse : []);
      }

      const files = rawFiles.map((f: any): AudioFile => ({
        name: f.filename || f.name || f.s3_key?.split('/').pop() || 'recording.wav',
        id: f.s3_key || f.id || f.key || '',
        created_at: f.last_modified || f.created_at || new Date().toISOString(),
        size: f.size_bytes || f.size || 0,
        survey_reference: (f.survey_reference || f.survey_type || '').replace(/^ref\s*:\s*/i, '').trim(),
        uuid: f.patient_uuid || f.uuid || f.patient_id || '',
        publicUrl: f.url || f.public_url || f.publicUrl
      }));

      const grouped = new Map<string, AudioFile[]>();
      files.forEach((f: any) => {
        if (!grouped.has(f.uuid)) grouped.set(f.uuid, []);
        grouped.get(f.uuid)!.push(f);
      });

      const initial: PatientAudio[] = Array.from(grouped.entries()).map(([patientId, audioFiles]) => ({
        patientId,
        patient: null,
        audioFiles,
        isExpanded: false,
      }));

      setPatientAudios(initial);
      setFilteredPatientAudios(initial);

      const patientIds = Array.from(grouped.keys());
      await Promise.allSettled(
        patientIds.map(async (patientId) => {
          try {
            const patient = await AuthService.getPatient(patientId);
            if (patient) {
              const info: PatientInfo = {
                id: patient.id,
                first_name: patient.first_name ?? null,
                middle_name: patient.middle_name ?? null,
                last_name: patient.last_name ?? null,
                email: patient.email ?? null,
                mobile_number: patient.mobile_number ?? null,
                date_of_birth: patient.date_of_birth ?? null,
                gender: patient.gender ?? null,
              };
              const patch = (list: PatientAudio[]) =>
                list.map(pa => pa.patientId === patientId ? { ...pa, patient: info } : pa);
              setPatientAudios(patch);
              setFilteredPatientAudios(patch);
            }
          } catch {
            // Patient not found — UUID shown as fallback label
          }
        })
      );
    } catch (err) {
      console.error('Error fetching patient audios:', err);
      toast.error('Failed to load patient audio files', { id: 'fetch-audio-error' });
    } finally {
      setLoading(false);
    }
  }, [navigate, selectedLocationId, locations]);

  useEffect(() => {
    if (branchLoading) return;
    fetchPatientAudios();
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, [fetchPatientAudios, branchLoading]);

  // ── Filter & Sort ──────────────────────────────────────────────────────────

  useEffect(() => {
    let result = patientAudios;

    // 1. Text search (patient name, ID, email, phone, filename, survey ref)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(pa => {
        const name = getPatientDisplayName(pa.patient, pa.patientId).toLowerCase();
        const email = pa.patient?.email?.toLowerCase() || '';
        const phone = pa.patient?.mobile_number?.toLowerCase() || '';
        const pid = pa.patientId.toLowerCase();
        const hasFile = pa.audioFiles.some(f =>
          f.name.toLowerCase().includes(q) || (f.survey_reference && f.survey_reference.toLowerCase().includes(q))
        );
        return name.includes(q) || email.includes(q) || phone.includes(q) || pid.includes(q) || hasFile;
      });
    }

    // 2. Date filter (today, week, month)
    if (dateFilter !== 'all') {
      const now = new Date();
      result = result.map(pa => {
        const matchingFiles = pa.audioFiles.filter(f => {
          const fileDate = new Date(f.created_at);
          if (dateFilter === 'today') {
            return fileDate.toDateString() === now.toDateString();
          }
          if (dateFilter === 'week') {
            const weekAgo = new Date();
            weekAgo.setDate(now.getDate() - 7);
            return fileDate >= weekAgo;
          }
          if (dateFilter === 'month') {
            const monthAgo = new Date();
            monthAgo.setMonth(now.getMonth() - 1);
            return fileDate >= monthAgo;
          }
          return true;
        });
        return { ...pa, audioFiles: matchingFiles };
      }).filter(pa => pa.audioFiles.length > 0);
    }

    // 3. Survey reference filter
    if (surveyFilter !== 'all') {
      result = result.map(pa => {
        const matchingFiles = pa.audioFiles.filter(f => f.survey_reference === surveyFilter);
        return { ...pa, audioFiles: matchingFiles };
      }).filter(pa => pa.audioFiles.length > 0);
    }

    // 4. Sort
    const sorted = [...result].sort((a, b) => {
      if (sortBy === 'name') {
        const aVal = getPatientDisplayName(a.patient, a.patientId).toLowerCase();
        const bVal = getPatientDisplayName(b.patient, b.patientId).toLowerCase();
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortBy === 'files') {
        const aCount = a.audioFiles.length;
        const bCount = b.audioFiles.length;
        return sortOrder === 'asc' ? aCount - bCount : bCount - aCount;
      }
      // Date sort (default)
      const aDate = a.audioFiles[0]?.created_at ? new Date(a.audioFiles[0].created_at).getTime() : 0;
      const bDate = b.audioFiles[0]?.created_at ? new Date(b.audioFiles[0].created_at).getTime() : 0;
      return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
    });

    setFilteredPatientAudios(sorted);
  }, [searchQuery, dateFilter, surveyFilter, sortBy, sortOrder, patientAudios]);

  // ── Expand / Collapse ──────────────────────────────────────────────────────

  const toggleExpansion = (patientId: string) => {
    const toggle = (list: PatientAudio[]) =>
      list.map(pa => pa.patientId === patientId ? { ...pa, isExpanded: !pa.isExpanded } : pa);
    setPatientAudios(toggle);
    setFilteredPatientAudios(toggle);
  };

  // ── Audio URL Resolution (Presigned URL + /audio-stream fallback) ──────────

  const getAudioPlayUrl = async (file: AudioFile): Promise<string> => {
    // 1. If file already has a valid direct public URL, use it
    if (file.publicUrl && typeof file.publicUrl === 'string' && file.publicUrl.startsWith('http')) {
      return file.publicUrl;
    }

    // 2. Try refreshing presigned URL from backend
    try {
      const res: any = await AuthService.refreshAudioPresignUrl(file.id);
      const presignedUrl = res?.data?.url || res?.url;
      if (presignedUrl && typeof presignedUrl === 'string' && presignedUrl.startsWith('http')) {
        return presignedUrl;
      }
    } catch (e) {
      console.warn('Presign refresh failed, falling back to /audio-stream...', e);
    }

    // 3. Fallback: Stream directly as binary Blob via /audio-stream
    const token = await biometricAuthService.getBiometricToken();
    const baseUrl = ENV.VITE_AWS_API_GATEWAY_URL.replace(/\/$/, '');
    const streamRes = await fetch(`${baseUrl}/audio-stream?key=${encodeURIComponent(file.id)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    if (!streamRes.ok) {
      throw new Error(`Audio stream failed with status ${streamRes.status}`);
    }

    const blob = await streamRes.blob();
    return URL.createObjectURL(blob);
  };

  // ── Play / Pause / Seek ───────────────────────────────────────────────────

  const togglePlay = async (file: AudioFile) => {
    // 1. If currently active file, toggle play / pause
    if (currentPlayingId === file.id && audioRef.current) {
      if (audioRef.current.paused) {
        try {
          await audioRef.current.play();
          setIsAudioPlaying(true);
        } catch (e) {
          console.error('Play resume error:', e);
        }
      } else {
        audioRef.current.pause();
        setIsAudioPlaying(false);
      }
      return;
    }

    // 2. Switching to new audio or starting fresh
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setCurrentPlayingId(null);
    setIsAudioPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setLoadingUrl(file.id);

    try {
      const playUrl = await getAudioPlayUrl(file);
      if (!playUrl) throw new Error('No audio URL returned from server');

      const audio = new Audio(playUrl);
      audioRef.current = audio;

      const updateDuration = () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
        }
      };

      audio.onloadedmetadata = updateDuration;
      audio.ondurationchange = updateDuration;

      audio.ontimeupdate = () => {
        setCurrentTime(audio.currentTime);
        updateDuration();
      };

      audio.onended = () => {
        setIsAudioPlaying(false);
        setCurrentTime(0);
      };

      audio.onerror = () => {
        setIsAudioPlaying(false);
        setCurrentPlayingId(null);
        toast.error('Could not load audio file. Please try again.');
      };

      await audio.play();
      setCurrentPlayingId(file.id);
      setIsAudioPlaying(true);
    } catch (err) {
      console.error('Play error:', err);
      setCurrentPlayingId(null);
      setIsAudioPlaying(false);
      toast.error('Could not load audio recording.');
    } finally {
      setLoadingUrl(null);
    }
  };

  const handleSeek = (newTime: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleSkip = (seconds: number) => {
    if (audioRef.current) {
      const maxDur = duration || audioRef.current.duration || 999999;
      const targetTime = Math.min(maxDur, Math.max(0, audioRef.current.currentTime + seconds));
      audioRef.current.currentTime = targetTime;
      setCurrentTime(targetTime);
    }
  };

  // ── Download ───────────────────────────────────────────────────────────────

  const handleDownload = async (file: AudioFile, patientName: string) => {
    try {
      const playUrl = await getAudioPlayUrl(file);
      if (!playUrl) throw new Error('No download URL');
      
      const safeName = (patientName || 'patient').replace(/[^a-zA-Z0-9_-]/g, '_');
      const a = document.createElement('a');
      a.href = playUrl;
      a.download = `${safeName}_${file.name}`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download audio file');
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async (file: AudioFile) => {
    if (!window.confirm(`Are you sure you want to delete ${file.name}?`)) return;
    try {
      setDeletingFile(file.id);
      // patient_uuid is stored as file.uuid in our mapped AudioFile type
      const res = await AuthService.deleteAudioFile(file.uuid, file.id);
      
      if (res.error) throw new Error(res.error);

      const remove = (list: PatientAudio[]) =>
        list
          .map(pa => ({ ...pa, audioFiles: pa.audioFiles.filter(f => f.id !== file.id) }))
          .filter(pa => pa.audioFiles.length > 0);
      setPatientAudios(remove);
      setFilteredPatientAudios(remove);

      if (currentPlayingId === file.id) {
        audioRef.current?.pause();
        audioRef.current = null;
        setCurrentPlayingId(null);
        setIsAudioPlaying(false);
        setCurrentTime(0);
        setDuration(0);
      }
      toast.success('File deleted successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete file');
    } finally {
      setDeletingFile(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a7a4a]" />
        </div>
      </MainLayout>
    );
  }

  const availableSurveys = Array.from(
    new Set(
      patientAudios.flatMap(pa => pa.audioFiles.map(f => f.survey_reference)).filter(Boolean)
    )
  );

  const totalRecordings = patientAudios.reduce((acc, pa) => acc + pa.audioFiles.length, 0);
  const filteredRecordings = filteredPatientAudios.reduce((acc, pa) => acc + pa.audioFiles.length, 0);
  const hasActiveFilters = searchQuery.trim() !== '' || dateFilter !== 'all' || surveyFilter !== 'all';

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f7f8fa] p-4 sm:p-6 font-sans">
        <div className="max-w-7xl mx-auto space-y-4 sm:space-y-5">
          {/* Header & Stats */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                    Audio Archives
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                    {canListen
                      ? 'Listen to, download, and manage patient voice recordings'
                      : 'Manage patient voice recordings — listen access restricted to clinical staff'}
                  </p>
                </div>

                {/* Quick Stats */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs py-1 px-3 bg-white border-gray-200 text-gray-700 shadow-2xs font-normal">
                    <User className="h-3.5 w-3.5 mr-1.5 text-emerald-600" />
                    <span className="font-semibold text-gray-900 mr-1">{filteredPatientAudios.length}</span>
                    {filteredPatientAudios.length !== patientAudios.length ? `of ${patientAudios.length} ` : ''}Patients
                  </Badge>
                  <Badge variant="outline" className="text-xs py-1 px-3 bg-white border-gray-200 text-gray-700 shadow-2xs font-normal">
                    <Volume2 className="h-3.5 w-3.5 mr-1.5 text-emerald-600" />
                    <span className="font-semibold text-gray-900 mr-1">{filteredRecordings}</span>
                    {filteredRecordings !== totalRecordings ? `of ${totalRecordings} ` : ''}Recordings
                  </Badge>
                </div>
              </div>

              {/* Filter Toolbar */}
              <div className="bg-white rounded-xl border border-gray-200/80 p-2.5 sm:p-3 shadow-xs space-y-2.5">
                <div className="flex flex-col md:flex-row md:items-center gap-2.5">
                  {/* Search input with clear button */}
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="search"
                      placeholder="Search by patient name, phone, survey ref, filename..."
                      className="pl-9 pr-8 rounded-lg border-gray-200 bg-gray-50/50 h-9 text-xs sm:text-sm focus:bg-white focus:ring-1 focus:ring-[#1a7a4a]"
                      value={searchQuery}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 cursor-pointer"
                        title="Clear search"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Date Filter Pills */}
                  <div className="flex items-center gap-1 bg-gray-100/80 p-0.5 rounded-lg text-xs self-start md:self-auto overflow-x-auto">
                    {[
                      { id: 'all', label: 'All Time' },
                      { id: 'today', label: 'Today' },
                      { id: 'week', label: '7 Days' },
                      { id: 'month', label: '30 Days' },
                    ].map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setDateFilter(t.id as any)}
                        className={`px-2.5 py-1 rounded-md font-medium transition-colors text-xs whitespace-nowrap cursor-pointer ${
                          dateFilter === t.id
                            ? 'bg-white text-emerald-700 font-semibold shadow-2xs'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {/* Survey Reference Filter */}
                  {availableSurveys.length > 0 && (
                    <div className="flex items-center">
                      <select
                        value={surveyFilter}
                        onChange={(e) => setSurveyFilter(e.target.value)}
                        className="h-9 px-2.5 text-xs rounded-lg border border-gray-200 bg-gray-50/50 text-gray-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#1a7a4a]"
                      >
                        <option value="all">All Tests ({availableSurveys.length})</option>
                        {availableSurveys.map((ref) => (
                          <option key={ref} value={ref}>
                            {ref.replace(/^ref\s*:\s*/i, '')}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Sort Selector */}
                  <div className="flex items-center gap-1.5 self-start md:self-auto">
                    <select
                      value={`${sortBy}-${sortOrder}`}
                      onChange={(e) => {
                        const [sb, so] = e.target.value.split('-');
                        setSortBy(sb as any);
                        setSortOrder(so as any);
                      }}
                      className="h-9 px-2.5 text-xs rounded-lg border border-gray-200 bg-gray-50/50 text-gray-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#1a7a4a]"
                    >
                      <option value="date-desc">Newest First</option>
                      <option value="date-asc">Oldest First</option>
                      <option value="name-asc">Patient Name (A-Z)</option>
                      <option value="name-desc">Patient Name (Z-A)</option>
                      <option value="files-desc">Most Recordings</option>
                    </select>

                    {hasActiveFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSearchQuery('');
                          setDateFilter('all');
                          setSurveyFilter('all');
                        }}
                        className="h-9 px-2 text-xs text-gray-500 hover:text-gray-800 gap-1"
                        title="Reset all filters"
                      >
                        <RotateCcw className="h-3 w-3" />
                        <span className="hidden sm:inline">Reset</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Role badge */}
              {!canListen && (
                <div className="flex items-center gap-2 px-3.5 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 w-fit">
                  <span className="font-semibold">Non-clinical view:</span> You can delete files but cannot listen or download recordings.
                </div>
              )}

              {/* Patient List (Expandable Cards with Completion Bar) */}
              <div className="space-y-4">
                {filteredPatientAudios.length > 0 ? (
                  filteredPatientAudios.map((patientAudio) => (
                    <Card
                      key={patientAudio.patientId}
                      className="rounded-2xl border-gray-100 shadow-sm overflow-hidden bg-white hover:border-[#1a7a4a]/20 transition-all duration-200"
                    >
                      <Collapsible
                        open={patientAudio.isExpanded}
                        onOpenChange={() => toggleExpansion(patientAudio.patientId)}
                      >
                        <CollapsibleTrigger asChild>
                          <div className="px-6 py-5 hover:bg-gray-50/50 cursor-pointer transition-colors border-b border-transparent data-[state=open]:border-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {patientAudio.isExpanded
                                  ? <ChevronDown className="h-5 w-5 flex-shrink-0 text-gray-500" />
                                  : <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-500" />}

                                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <User className="h-5 w-5 text-primary" />
                                </div>

                                <div>
                                  <CardTitle className="text-base leading-tight">
                                    {patientAudio.patient
                                      ? getPatientDisplayName(patientAudio.patient, patientAudio.patientId)
                                      : <span className="font-mono text-sm text-muted-foreground">{patientAudio.patientId}</span>
                                    }
                                  </CardTitle>
                                  {patientAudio.patient && (
                                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                      {patientAudio.patient.email && (
                                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                          <Mail className="h-3 w-3" />
                                          {patientAudio.patient.email}
                                        </span>
                                      )}
                                      {patientAudio.patient.mobile_number && (
                                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                          <Phone className="h-3 w-3" />
                                          {formatPhoneDisplay(patientAudio.patient.mobile_number)}
                                        </span>
                                      )}
                                      {patientAudio.patient.date_of_birth && (
                                        <span className="text-xs text-muted-foreground">
                                          DOB: {format(new Date(patientAudio.patient.date_of_birth), 'MMM d, yyyy')}
                                        </span>
                                      )}
                                      {patientAudio.patient.gender && (
                                        <span className="text-xs text-muted-foreground capitalize">
                                          {patientAudio.patient.gender}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-4 text-sm text-muted-foreground flex-shrink-0">
                                <div className="flex items-center gap-1">
                                  <Folder className="h-4 w-4" />
                                  <span>{patientAudio.audioFiles.length} files</span>
                                </div>
                                {patientAudio.audioFiles[0] && (
                                  <span className="hidden sm:inline">
                                    Latest: {format(new Date(patientAudio.audioFiles[0].created_at), 'MMM d, yyyy')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <CardContent className="pt-0 pb-4 px-6">
                            <div className="space-y-2.5 mt-2">
                              {patientAudio.audioFiles.map((file) => {
                                const isActive = currentPlayingId === file.id;
                                const isPlaying = isActive && isAudioPlaying;
                                const isLoadingThis = loadingUrl === file.id;
                                const progressPercent = isActive && duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
                                const testName = file.survey_reference ? file.survey_reference.replace(/^ref\s*:\s*/i, '').trim() : null;

                                return (
                                  <div
                                    key={file.id}
                                    className={`group relative rounded-2xl border transition-all duration-200 overflow-hidden ${
                                      isActive
                                        ? 'bg-gradient-to-b from-emerald-50/50 via-teal-50/20 to-white border-emerald-400 shadow-md shadow-emerald-500/5 ring-2 ring-emerald-500/15 p-4 sm:p-5'
                                        : 'bg-white hover:bg-slate-50/70 border-slate-200/80 hover:border-emerald-500/30 hover:shadow-xs p-4 sm:p-5'
                                    }`}
                                  >
                                    <div className="flex flex-col gap-3.5">
                                      {/* Main Row: Controller + Title & Details + Actions */}
                                      <div className="flex items-start sm:items-center justify-between gap-3">
                                        <div className="flex items-center gap-3.5 min-w-0 flex-1">
                                          {/* Tactile Circular Play/Pause Controller */}
                                          {canListen && (
                                            <button
                                              type="button"
                                              onClick={() => togglePlay(file)}
                                              disabled={isLoadingThis}
                                              className={`h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer ${
                                                isPlaying
                                                  ? 'bg-gradient-to-tr from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-600/30 ring-4 ring-emerald-500/20 scale-105 active:scale-95'
                                                  : isActive
                                                  ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20 ring-2 ring-emerald-500/30 hover:bg-emerald-700'
                                                  : 'bg-slate-100 text-slate-700 border border-slate-200 shadow-2xs hover:bg-emerald-600 hover:text-white hover:border-emerald-600 hover:shadow-sm group-hover:scale-105'
                                              }`}
                                              title={isPlaying ? 'Pause' : 'Play'}
                                            >
                                              {isLoadingThis ? (
                                                <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                                              ) : isPlaying ? (
                                                <Pause className="h-5 w-5 fill-current" />
                                              ) : (
                                                <Play className="h-5 w-5 ml-0.5 fill-current" />
                                              )}
                                            </button>
                                          )}

                                          {/* Title & Metadata */}
                                          <div className="min-w-0 flex-1 space-y-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              {/* Test / Survey Name as primary badge or title */}
                                              {testName ? (
                                                <>
                                                  <span className="font-semibold text-sm sm:text-base text-gray-900 tracking-tight">
                                                    {testName}
                                                  </span>
                                                  <span className="font-mono text-[11px] text-gray-400 bg-gray-50 border border-gray-200/80 px-1.5 py-0.5 rounded truncate max-w-[200px]" title={file.name}>
                                                    {file.name}
                                                  </span>
                                                </>
                                              ) : (
                                                <span className="font-semibold text-sm sm:text-base text-gray-900 truncate max-w-sm" title={file.name}>
                                                  {file.name}
                                                </span>
                                              )}

                                              {/* Live Equalizer Pulse Indicator */}
                                              {isPlaying && (
                                                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-100/90 border border-emerald-200 text-emerald-800 text-[11px] font-medium animate-in fade-in duration-200">
                                                  <div className="flex items-end gap-0.5 h-3">
                                                    <span className="w-0.5 h-full bg-emerald-600 rounded-full animate-[pulse_0.6s_ease-in-out_infinite]" />
                                                    <span className="w-0.5 h-2/3 bg-emerald-600 rounded-full animate-[pulse_0.8s_ease-in-out_infinite]" />
                                                    <span className="w-0.5 h-full bg-emerald-600 rounded-full animate-[pulse_0.5s_ease-in-out_infinite]" />
                                                  </div>
                                                  <span>Playing</span>
                                                </div>
                                              )}

                                              {isActive && !isPlaying && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100/90 border border-amber-200 text-amber-800 text-[11px] font-medium">
                                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                                  Paused
                                                </span>
                                              )}
                                            </div>

                                            {/* Metadata Chips: Date & Size */}
                                            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                                              <span className="flex items-center gap-1 text-gray-500">
                                                <Clock className="h-3 w-3 text-gray-400" />
                                                {format(new Date(file.created_at), 'MMM d, yyyy • h:mm a')}
                                              </span>
                                              <span className="text-gray-300">•</span>
                                              <span className="text-gray-500 font-medium">{formatFileSize(file.size)}</span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Right Action Buttons */}
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                          {canListen && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => handleDownload(file, getPatientDisplayName(patientAudio.patient, patientAudio.patientId))}
                                              className="h-8 px-2.5 rounded-xl text-xs gap-1.5 text-gray-700 hover:text-emerald-700 hover:border-emerald-300 bg-white shadow-2xs transition-colors"
                                              title="Download recording"
                                            >
                                              <Download className="h-3.5 w-3.5" />
                                              <span className="hidden md:inline font-medium">Download</span>
                                            </Button>
                                          )}
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDelete(file)}
                                            className="h-8 w-8 p-0 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                            title="Delete recording"
                                            disabled={deletingFile === file.id}
                                          >
                                            {deletingFile === file.id ? (
                                              <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                                            ) : (
                                              <Trash2 className="h-4 w-4" />
                                            )}
                                          </Button>
                                        </div>
                                      </div>

                                      {/* Completion & Scrubber Bar Below */}
                                      {canListen && (
                                        <div className={`pt-3 border-t transition-colors ${isActive ? 'border-emerald-200/60' : 'border-gray-100'}`}>
                                          <div className="flex items-center gap-3">
                                            {/* Current Time */}
                                            <span className={`text-xs font-mono font-semibold w-11 text-right tabular-nums select-none ${
                                              isActive ? 'text-emerald-800' : 'text-gray-400'
                                            }`}>
                                              {isActive ? formatAudioTime(currentTime) : '0:00'}
                                            </span>

                                            {/* Interactive Seek Bar */}
                                            <div className="relative flex-1 flex items-center h-5 group/scrubber">
                                              <input
                                                type="range"
                                                min={0}
                                                max={isActive && duration > 0 ? duration : 100}
                                                step={0.1}
                                                value={isActive ? currentTime : 0}
                                                onChange={(e) => {
                                                  if (isActive) {
                                                    handleSeek(parseFloat(e.target.value));
                                                  }
                                                }}
                                                disabled={!isActive || !duration}
                                                className={`w-full h-2 rounded-full appearance-none cursor-pointer focus:outline-none transition-all ${
                                                  isActive ? 'accent-emerald-600 group-hover/scrubber:h-2.5' : 'accent-gray-300 opacity-50 cursor-not-allowed'
                                                }`}
                                                style={{
                                                  background: isActive && duration > 0
                                                    ? `linear-gradient(to right, #059669 0%, #0d9488 ${progressPercent}%, #e2e8f0 ${progressPercent}%, #e2e8f0 100%)`
                                                    : '#e2e8f0'
                                                }}
                                                title={isActive ? `Seek (${formatAudioTime(currentTime)} / ${formatAudioTime(duration)})` : 'Click Play to listen'}
                                              />
                                            </div>

                                            {/* Total Duration */}
                                            <span className={`text-xs font-mono font-semibold w-11 tabular-nums select-none ${
                                              isActive ? 'text-gray-700' : 'text-gray-400'
                                            }`}>
                                              {isActive && duration > 0 ? formatAudioTime(duration) : '--:--'}
                                            </span>

                                            {/* Quick Skip Buttons (-5s / +5s) when active */}
                                            {isActive && (
                                              <div className="hidden sm:flex items-center gap-1 pl-1 border-l border-emerald-200/60">
                                                <button
                                                  type="button"
                                                  onClick={() => handleSkip(-5)}
                                                  className="px-1.5 py-0.5 rounded text-[11px] font-mono font-medium text-gray-500 hover:text-emerald-700 hover:bg-emerald-100/60 transition-colors cursor-pointer"
                                                  title="Rewind 5 seconds"
                                                >
                                                  -5s
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => handleSkip(5)}
                                                  className="px-1.5 py-0.5 rounded text-[11px] font-mono font-medium text-gray-500 hover:text-emerald-700 hover:bg-emerald-100/60 transition-colors cursor-pointer"
                                                  title="Forward 5 seconds"
                                                >
                                                  +5s
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </Card>
                  ))
                ) : (
                  <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-8 text-center">
                    <Folder className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                    <h3 className="text-base font-semibold text-gray-900 mb-1">No recordings found</h3>
                    <p className="text-xs text-gray-400 max-w-sm mx-auto">
                      {hasActiveFilters
                        ? 'No audio recordings match your active filters or search criteria.'
                        : 'No audio recordings available for this branch yet.'}
                    </p>
                    {hasActiveFilters && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSearchQuery('');
                          setDateFilter('all');
                          setSurveyFilter('all');
                        }}
                        className="mt-3 text-xs h-8"
                      >
                        Reset filters
                      </Button>
                    )}
                  </div>
                )}
              </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default PatientAudioManager;